import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useState } from "react";
import TaxModal from "./components/TaxModal";
import GroupModal from "./components/GroupModal";
import TTax from "./types/TTax";
import TTaxGroup from "./types/TTaxGroup";
import ToggleSwitch from "components/toggle/ToggleSwitch";
import { FaArchive } from "react-icons/fa";
import SyncTax from "./components/SyncTax";

const TaxManagement = () => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isTaxModalOpen, setIsTaxModalOpen] = useState(false);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<TTaxGroup | null>(null);
  const [selectedTax, setSelectedTax] = useState<TTax | null>(null);
  const [selectedCombinedTaxRateId, setSelectedCombinedTaxRateId] = useState<number>(0);
  const [groupName, setGroupName] = useState("");
  const [selectedTaxes, setSelectedTaxes] = useState<any[]>([]);
  const [showArchivedTaxes, setShowArchivedTaxes] = useState(false);
  const [showArchivedGroups, setShowArchivedGroups] = useState(false);
  const [searchQueryTaxes, setSearchQueryTaxes] = useState("");
  const [searchQueryGroups, setSearchQueryGroups] = useState("");
  const [animatingTaxId, setAnimatingTaxId] = useState<string | null>(null);
  const [animatingTaxGroupId, setAnimatingTaxGroupId] = useState<string | null>(null);
  const [selectedTabs, setSelectedTabs] = useState<string[]>(["all", "discount", "fee", "tax"]);
  const [selectedTabsGroup, setSelectedTabsGroup] = useState<string[]>(["group", "combined tax rate"]);

  const queryClient = useQueryClient();

  const getTaxes = async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/company/tax/dashboard/true`
    );
    return response.data;
  };

  const { data, isLoading } = useQuery("taxes", getTaxes, { refetchOnWindowFocus: false });
  
  const mutation = useMutation(
    (newGroup: { label: string; taxIds: number[]; combinedTaxRateId: number; taxOrderings: number[] }) => {
      if (isEditMode && selectedGroup) {
        return axios.put(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/tax_groups`,
          {
            id: selectedGroup.id,
            label: newGroup.label,
            taxIds: newGroup.taxIds,
            combinedTaxRateId: newGroup.combinedTaxRateId,
            taxOrderings: newGroup.taxOrderings,
          }
        );
      } else {
        return axios.post(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/tax_groups`,
          newGroup
        );
      }
    },
    {
      onSuccess: async (_, variables) => {
        console.log("variables", variables);
        if (isEditMode && selectedGroup) {
          syncTaxGroup(selectedGroup);
        }
        queryClient.invalidateQueries("taxes");
        setIsGroupModalOpen(false);
        setSelectedTaxes([]);
        setGroupName("");
        setSelectedGroup(null);
        setIsEditMode(false);
      },
      onError: (err) => {
        console.error("Failed to save tax group:", err);
      },
    }
  );

  const { mutate: syncTax, isLoading: syncTaxLoading, variables: syncingTaxId } = useMutation(
    (taxId: string) => {
      return axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/sync-tax-rate`,
        { taxId }
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("taxes");
      },
      onError: (err) => {
        queryClient.invalidateQueries("taxes");
        console.error("Failed to sync tax:", err);
      },
      onSettled: (_, __, variables) => {
        setAnimatingTaxId(variables);
        setTimeout(() => {
          setAnimatingTaxId(null);
        }, 1000);
      }
    },
  );

  const { mutate: syncTaxGroup, isLoading: syncTaxGroupLoading, variables: syncingTaxGroup } = useMutation(
    (taxGroup: TTaxGroup) => {
      return axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/sync-tax-group`,
        { taxes: taxGroup.taxes, label: taxGroup.label }
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("taxes");
      },
      onError: (err) => {
        queryClient.invalidateQueries("taxes");
        console.error("Failed to sync tax group:", err);
      },
      onSettled: (_, __, variables) => {
        setAnimatingTaxGroupId(variables.id.toString());
        setTimeout(() => {
          setAnimatingTaxGroupId(null);
        }, 1000);
      }
    }
  );

  const syncingTaxGroupId =
    syncingTaxGroup && typeof syncingTaxGroup === "object"
      ? syncingTaxGroup.id?.toString() ?? ""
      : "";

  const currentSyncingGroup: TTaxGroup | null =
    syncTaxGroupLoading && syncingTaxGroup && typeof syncingTaxGroup === "object"
      ? (syncingTaxGroup as TTaxGroup)
      : null;

  const taxes = (data?.taxes as TTax[] ?? []).sort((a, b) => a.label.localeCompare(b.label));
  const taxGroups = (data?.taxGroups as TTaxGroup[] ?? []).sort((a, b) => a.label.localeCompare(b.label));

  const toggleGroupModal = (group?: TTaxGroup) => {
    if (group) {
      setIsEditMode(true);
      setGroupName(group.label);
      setSelectedTaxes(group.taxes);
      setSelectedGroup(group);
    } else {
      setIsEditMode(false);
      setGroupName("");
      setSelectedTaxes([]);
      setSelectedGroup(null);
    }
    setIsGroupModalOpen((prev) => !prev);
  };

  const toggleTaxModal = (tax?: TTax) => {
    if (tax) {
      setSelectedTax(tax);
    } else {
      setSelectedTax(null);
    }
    setIsTaxModalOpen((prev) => !prev);
  };

  const handleTaxSelection = (tax: any, isCombinedTaxRate: boolean = false, selectedTab?: string) => {
    if (selectedTab === "tax_group") { // add combined tax rate id to state for post request
      setSelectedCombinedTaxRateId(Number(tax.id));
    }
    if ((tax.taxes || tax.tax_type === "tax") && !isCombinedTaxRate) { // if tax or tax group only one total can be selected (unless it's a combined tax rate)
      const feesAndDiscounts = selectedTaxes.filter(t => 
        t.tax_type === "fee" || t.tax_type === "discount"
      );
      
      // Check if this tax/tax group is already selected
      const isAlreadySelected = selectedTaxes.some((t) => t.id === tax.id);
      
      if (isAlreadySelected) {
        // Remove it if already selected
        setSelectedTaxes(feesAndDiscounts);
      } else {
        // Add the new tax/tax group to fees and discounts
        setSelectedTaxes([...feesAndDiscounts, tax]);
      }
    } else {
      setSelectedTaxes((prevSelected) =>
        prevSelected.some((t) => t.id === tax.id)
          ? prevSelected.filter((t) => t.id !== tax.id)
          : [...prevSelected, tax]
      );
    }
  };  

    const handleSaveGroup = async () => {
      // Sort selected taxes by category: discounts first, then fees, then tax/combined tax rate
      const sortedTaxes = [...selectedTaxes].sort((a, b) => {
        const categoryOrder: { [key: string]: number } = { discount: 1, fee: 2, tax: 3 };
        const aCategory = a.tax_type || (a.taxes ? 'tax' : 'tax');
        const bCategory = b.tax_type || (b.taxes ? 'tax' : 'tax');
        
        if (categoryOrder[aCategory] !== categoryOrder[bCategory]) {
          return categoryOrder[aCategory] - categoryOrder[bCategory];
        }
        
        // If same category, maintain current order
        return selectedTaxes.indexOf(a) - selectedTaxes.indexOf(b);
      });

      const taxIds = sortedTaxes.map((tax) => Number(tax.id));
      const filteredTaxIds = taxIds.filter((id) => id !== selectedCombinedTaxRateId);
      const taxOrderings = sortedTaxes
        .filter((tax) => Number(tax.id) !== selectedCombinedTaxRateId)
        .map((_, index) => index + 1);

      if (groupName && selectedTaxes.length > 0) {
        mutation.mutate({ 
          label: groupName, 
          taxIds: filteredTaxIds, 
          combinedTaxRateId: selectedCombinedTaxRateId,
          taxOrderings: taxOrderings
        });
      }
    };

  const handleSelectedTab = (tab: string) => {
    if (tab === "all") {
      setSelectedTabs(prev => 
        prev.includes("all") 
          ? [] 
          : ["all", "discount", "fee", "tax"] 
      );
    } else {
      setSelectedTabs(prev => {
        const newTabs = prev.includes(tab) 
          ? prev.filter(t => t !== tab && t !== "all") 
          : [...prev.filter(t => t !== "all"), tab]; 
        

        if (newTabs.length === 3 && 
            newTabs.includes("discount") && 
            newTabs.includes("fee") && 
            newTabs.includes("tax")) {
          return [...newTabs, "all"];
        }

        return newTabs;
      });
    }
  };

  const handleSelectedTabGroup = (tab: string) => {
    setSelectedTabsGroup(prev => {
      if (prev.includes(tab)) {
        return prev.filter(t => t !== tab);
      } else {
        return [...prev, tab];
      }
    });
  };

  const filteredTaxes = taxes
    .filter((tax) => {
      const matchesArchive = showArchivedTaxes || !tax.archived;
      const matchesSearch = tax.label.toLowerCase().includes(searchQueryTaxes.toLowerCase());
      const matchesTab = selectedTabs.length === 0 ? false : (selectedTabs.includes("all") || selectedTabs.includes(tax.tax_type));
      
      return matchesArchive && matchesSearch && matchesTab;
    })
    .sort((a, b) => a.label.localeCompare(b.label));

    const filteredTaxGroups = taxGroups
    .filter((group) => {
      const matchesArchive = showArchivedGroups || !group.archived;
      const matchesSearch = group.label.toLowerCase().includes(searchQueryGroups.toLowerCase());
      
      let matchesTab = false;

      if (selectedTabsGroup.includes("combined tax rate") &&
          group.taxes.every((tax) => tax.tax_type === "tax")) {
        matchesTab = true;
      }
      
      if (selectedTabsGroup.includes("group") &&
          group.taxes.some((tax) => tax.tax_type !== "tax")) {
        matchesTab = true;
      }
      
      return matchesArchive && matchesSearch && matchesTab;
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-2xl font-bold mb-4">Tax Management</h1>
      <button
        onClick={() => toggleTaxModal()}
        className="mb-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
      >
        New Item
      </button>
      <button
        onClick={() => toggleGroupModal()}
        className="mb-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 mx-4"
      >
        New Group
      </button>

      {isLoading ? (
        <div className="text-center text-gray-600">Loading...</div>
      ) : (
        <div className="flex gap-6 h-[600px]">

          {/* Taxes Section (Left Side) */}
          <div className="w-1/2 flex flex-col border border-gray-300 rounded-lg shadow-md p-4 overflow-auto" style={{ scrollbarGutter: "stable" }}>
            <div className="flex items-center justify-between space-x-2 mb-2">
              <h2 className="text-xl font-semibold">Taxes</h2>
              <div className="flex items-center space-x-2">
                <div className="pt-0.5 text-sm text-gray-500">Filters</div>
                {["discount", "fee", "tax", "all"].map((type) => (
                  <button 
                    key={type} 
                    className={`px-4 py-0.5 text-sm capitalize
                      ${selectedTabs.includes(type) ? "bg-blue-500 text-white rounded hover:bg-blue-600" : "bg-gray-200 text-gray-500 rounded hover:bg-gray-300"}
                    `}
                    onClick={() => handleSelectedTab(type)}
                  >
                    {type === "tax" ? "taxes" : type === "all" ? "all" : `${type}s`}
                  </button>
                ))}
              </div>
            </div>

            {/* Filters for Taxes */}
            <div className="flex items-center mb-4 space-x-4">
              <input
                type="text"
                placeholder="Search taxes..."
                value={searchQueryTaxes}
                onChange={(e) => setSearchQueryTaxes(e.target.value)}
                className="p-2 border border-gray-300 rounded w-64"
              />
              <ToggleSwitch
                checked={showArchivedTaxes}
                onChange={() => setShowArchivedTaxes((prev) => !prev)}
                label="Show Archived"
              />
            </div>

            <div className="space-y-4">
              {filteredTaxes.map((tax) => {
                const rates = [
                  { label: "List", rate: tax.list_rate },
                  { label: "Sale", rate: tax.sale_rate },
                  { label: "Install", rate: tax.install_rate },
                ].filter((r) => r.rate !== undefined && r.rate !== null);

                const uniqueRates = new Set(rates.map((r) => Number(r.rate)));

                return (
                  <div key={tax.id} className="relative p-4 bg-white rounded-lg shadow-md border border-gray-200">
                  <div className="flex justify-between items-center">
                    <div className="font-medium text-gray-700">{tax.label}</div>
                    <div className="flex items-center space-x-3">
                      {tax.archived && <FaArchive className="text-red-500 w-5 h-5" />}
                      <button className="text-blue-500 hover:underline" onClick={() => toggleTaxModal(tax)}>
                        Edit
                      </button>
                    </div>
                  </div>

                  {/* Rate Display Logic */}
                  <div className="text-gray-500">
                    {uniqueRates.size === 1 ? (
                      <span>
                        Rate: {tax.list_type === "fixed"
                          ? `$${(Number([...uniqueRates][0]) / 100).toFixed(2)}`
                          : `${Number([...uniqueRates][0])}%`}
                      </span>
                    ) : (
                      rates.map((r) => (
                        (r.rate > 0 || r.label !== 'List') && (
                          <div key={r.label}>
                            {r.label} Rate: {tax.list_type === "fixed"
                              ? `$${(Number(r.rate) / 100).toFixed(2)}`
                              : `${Number(r.rate)}%`}
                          </div>
                        )
                      ))
                    )}
                  </div>

                  <div className="text-gray-500">Type: {tax.list_type}</div>
                  <div className="flex justify-between items-center">
                    <div className="text-gray-500">Category: {tax.tax_type}</div>
                    <SyncTax 
                      tax={tax} 
                      animatingTaxId={animatingTaxId} 
                      syncTax={syncTax} 
                      syncTaxLoading={syncTaxLoading} 
                      syncingTaxId={syncingTaxId?.toString() ?? ""} 
                    />
                  </div>
                </div>
                );
              })}
            </div>

          </div>

          {/* Tax Groups Section (Right Side) */}
          <div className="w-1/2 flex flex-col border border-gray-300 rounded-lg shadow-md p-4 overflow-auto" style={{ scrollbarGutter: "stable" }}>
            <div className="flex items-center justify-between space-x-2 mb-2">
              <h2 className="text-xl font-semibold mb-2">Tax Groups</h2>
              <div className="flex items-center space-x-2">
                <div className="pt-0.5 text-sm text-gray-500">Filters</div>
                {["group", "combined tax rate"].map((type) => (
                  <button 
                    key={type} 
                    className={`px-4 py-0.5 text-sm capitalize
                      ${selectedTabsGroup.includes(type) ? "bg-blue-500 text-white rounded hover:bg-blue-600" : "bg-gray-200 text-gray-500 rounded hover:bg-gray-300"}
                    `}
                    onClick={() => handleSelectedTabGroup(type)}
                  >
                    {type === "all" ? "all" : `${type}s`}
                  </button>
                ))}
              </div>
            </div>

            {/* Filters for Tax Groups */}
            <div className="flex items-center mb-4 space-x-4">
              <input
                type="text"
                placeholder="Search tax groups..."
                value={searchQueryGroups}
                onChange={(e) => setSearchQueryGroups(e.target.value)}
                className="p-2 border border-gray-300 rounded w-64"
              />
              <ToggleSwitch
                checked={showArchivedGroups}
                onChange={() => setShowArchivedGroups((prev) => !prev)}
                label="Show Archived"
              />
            </div>

            <div className="space-y-4">
              {filteredTaxGroups.map((taxGroup) => (
                <div key={taxGroup.id} className="relative p-4 bg-white rounded-lg shadow-md border border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700">{taxGroup.label}</span>
                    <div className="flex items-center space-x-3">
                      {taxGroup.archived && ( <FaArchive className="text-red-500 w-5 h-5" />)}
                      <button
                        className="text-blue-500 hover:underline"
                        onClick={() => toggleGroupModal(taxGroup)}
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-between mt-2 items-center">
                    <div className="text-gray-500">{taxGroup.taxes.length} Taxes</div>
                    <div className="flex items-center space-x-2">
                      {taxGroup.taxes.every((tax) => tax.tax_type === "tax") && (
                        <SyncTax 
                          tax={taxGroup} 
                          animatingTaxId={animatingTaxGroupId} 
                          syncTax={syncTaxGroup} 
                          syncTaxLoading={syncTaxGroupLoading} 
                          syncingTaxId={syncingTaxGroupId} 
                          currentSyncingGroup={currentSyncingGroup}
                          group={true}
                        />
                      )}
                    </div>
                  </div>
                  <div className="mt-2 space-y-2">
                    {taxGroup.taxes.map((tax) => (
                      <div key={tax.id} className="p-2 bg-gray-50 rounded-lg border border-gray-100">
                        <div className="font-medium text-gray-700">{tax.label}</div>
                        <div className="text-gray-500">Rate: {tax.list_rate}%</div>
                        <div className="text-gray-500">Type: {tax.list_type}</div>
                        <div className="flex justify-between items-center">
                        <div className="text-gray-500">Category: {tax.tax_type}</div>
                          <SyncTax 
                            tax={tax} 
                            animatingTaxId={animatingTaxId} 
                            syncTax={syncTax} 
                            syncTaxLoading={syncTaxLoading} 
                            syncingTaxId={syncingTaxId?.toString() ?? ""} 
                            currentSyncingGroup={currentSyncingGroup}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {isTaxModalOpen && (
        <TaxModal
          taxes={taxes}
          isTaxModalOpen={isTaxModalOpen}
          setIsTaxModalOpen={setIsTaxModalOpen}
          selectedTax={selectedTax}
          setSelectedTax={setSelectedTax}
        />
      )}

      {isGroupModalOpen && (
        <GroupModal
          taxes={taxes}
          taxGroups={taxGroups}
          groupName={groupName}
          setGroupName={setGroupName}
          selectedTaxes={selectedTaxes}
          setSelectedTaxes={setSelectedTaxes}
          handleTaxSelection={handleTaxSelection}
          handleSaveGroup={handleSaveGroup}
          isEditMode={isEditMode}
          setIsGroupModalOpen={setIsGroupModalOpen}
          selectedGroup={selectedGroup}
          setSelectedGroup={setSelectedGroup}
          setSelectedCombinedTaxRateId={setSelectedCombinedTaxRateId}
        />
      )}
    </div >
  )
};

export default TaxManagement;